import type {
  AnalyticsTableCellDecorator,
  AnalyticsTableCellType,
  AnalyticsTableColumn,
} from '../../../../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'
import type { PrecisionValue } from '../../../../analyticsModule/components/tools/AnalyticsPrecisionSelect/AnalyticsPrecisionSelect.types'
import type {
  AnalyticsColumnConditionalVisibility,
  AnalyticsColumnVisibility,
  AnalyticsDataDecorationOverrides,
  AnalyticsFiltersForVisibility,
  AnalyticsReportDefinition,
} from '../../../../types'

const getIsHidden = (
  columnVisibility: AnalyticsColumnVisibility,
  filtersForVisibility: AnalyticsFiltersForVisibility,
): boolean => {
  if (!columnVisibility) return false

  if (columnVisibility.type !== 'conditional') return false

  const conditionalVisibility = columnVisibility as AnalyticsColumnConditionalVisibility

  const allConditionsMet = conditionalVisibility.conditions.every((condition) => {
    const filterGroupState = filtersForVisibility[condition.filterGroupKey]

    if (!filterGroupState) return false

    // For 'specific_selected', ignore filterGroupState.type and only check if all specificFilterKeys are selected
    if (
      condition.filterGroupSelectionOption === 'specific_selected' &&
      condition.specificFilterKeys &&
      condition.specificFilterKeys.length > 0
    ) {
      return condition.specificFilterKeys.every((key) => filterGroupState.options.includes(key))
    }

    // For other options, check if the selection state matches the required option
    const stateMatches = filterGroupState.type === condition.filterGroupSelectionOption

    return stateMatches
  })

  return !allConditionsMet
}

const PRECISION_LABELS = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
} as const

export const createAnalyticsTableColumns = ({
  definitionColumns,
  precision,
  decorationOverrides,
  filtersForVisibility,
}: {
  definitionColumns: AnalyticsReportDefinition['columns']
  precision?: PrecisionValue['value']
  decorationOverrides: AnalyticsDataDecorationOverrides
  filtersForVisibility: AnalyticsFiltersForVisibility
}): AnalyticsTableColumn[] => {
  const decorationOverridesMap = new Map(
    decorationOverrides?.map((override) => [override.columnKey, override]) || [],
  )

  return definitionColumns.map((column) => {
    const label =
      column.key === 'date' && precision ? (PRECISION_LABELS[precision] ?? 'Date') : column.label

    const decorationOverride = decorationOverridesMap.get(column.key)
    const decorator = decorationOverride?.decoration || column.decoration
    const isHidden = getIsHidden(column.visibility, filtersForVisibility)

    return {
      id: column.key,
      hidden: isHidden,
      label,
      type: column.type satisfies AnalyticsTableCellType,
      decorator: decorator ? (decorator satisfies AnalyticsTableCellDecorator) : undefined,
      tooltip: column.tooltip
        ? {
            title: 'title' in column.tooltip ? (column.tooltip.title as string) : undefined,
            description: column.tooltip.description,
            // TODO: Add formula
          }
        : undefined,
      breakdown: column.breakdown
        ? {
            label: column.breakdown.label ?? undefined,
            elements: column.breakdown.elements.map((element) => ({
              id: element.key,
              label: element.label,
            })),
          }
        : undefined,
    }
  })
}
